import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Chip, Separator, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import {
    renderWithKeyShortcuts,
    SALE_DISCOUNT_SHORTCUT,
    ShortcutHint,
} from '@/components/shortcuts';
import type { Discount } from '../../discountMath';
import { formatDiscountValue, formatGel } from '../../sellerMeta';
import type { CartNotice } from './useCartPanel';

const renderSaleDiscountButton = renderWithKeyShortcuts(
    'button',
    SALE_DISCOUNT_SHORTCUT.combination,
);

type CartTotalsProps = {
    isEmpty: boolean;
    subtotal: number;
    lineDiscountTotal: number;
    saleDiscount: Discount | null;
    saleDiscountAmount: number;
    /** The sale discount no longer fits: its tag turns neutral. */
    isSaleDiscountInvalid: boolean;
    /** A line's discount no longer fits: the line-discount sum is unknown. */
    isLineDiscountInvalid: boolean;
    total: number;
    /** Why payment is blocked; the total shows as unknown while it is set. */
    discountNotice: CartNotice | null;
    /** Opens the sale discount dialog, the same one `F7` opens. */
    onSaleDiscount: () => void;
};

/**
 * The cart's figures (SEL-100): Subtotal, the sum of list prices (muted);
 * Line discounts, only when above zero; the Sale discount row whenever the
 * cart holds something — an `Add` button with no sale discount, otherwise
 * its tag, its amount and an `Edit` button, both with the `F7` keycap; then
 * the Total, the panel's hero number. A discount that no longer fits shows
 * its notice under the figures (`role="alert"`), and every figure computed
 * through it reads as unknown, muted: the Line discounts sum when a line's
 * discount is the problem, the sale discount amount (its base is what the
 * lines leave) and the Total (QA #100).
 * Discount figures are in the default foreground: the warning-soft colour
 * falls to 3.91:1 on the dark panel. The minus sign, the labels and the
 * tag carry the meaning; the tag is a solid (`primary`) Chip, since the soft
 * one measures 3.61:1. A discount that no longer fits gets a neutral soft
 * tag (the soft danger Chip is 3.22:1 in every theme); the notice and the
 * unknown figures carry the problem.
 */
export const CartTotals: React.FC<CartTotalsProps> = ({
    isEmpty,
    subtotal,
    lineDiscountTotal,
    saleDiscount,
    saleDiscountAmount,
    isSaleDiscountInvalid,
    isLineDiscountInvalid,
    total,
    discountNotice,
    onSaleDiscount,
}) => {
    const { t } = useTranslation('pages');
    const unknown = t('seller.discount.unknown');
    // The sale discount applies to what the lines leave, so it is unknown
    // whenever any discount no longer fits.
    const isSaleAmountUnknown = discountNotice !== null;
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between">
                <Typography color="muted" type="body-sm">
                    {t('seller.subtotal')}
                </Typography>
                <Typography
                    className="tabular-nums"
                    color="muted"
                    {...getNodeTestIdProp('seller-subtotal')}
                    type="body-sm"
                >
                    {formatGel(subtotal)}
                </Typography>
            </div>

            {lineDiscountTotal > 0 ? (
                <div className="flex items-center justify-between">
                    <Typography type="body-sm">
                        {t('seller.cartDiscount.lineDiscounts')}
                    </Typography>
                    <Typography
                        className="tabular-nums"
                        color={isLineDiscountInvalid ? 'muted' : undefined}
                        {...getNodeTestIdProp('seller-line-discounts')}
                        type="body-sm"
                    >
                        {isLineDiscountInvalid
                            ? unknown
                            : `−${formatGel(lineDiscountTotal)}`}
                    </Typography>
                </div>
            ) : null}

            {isEmpty ? null : (
                <div
                    className="flex items-center justify-between gap-2"
                    {...getNodeTestIdProp('seller-sale-discount')}
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <Typography
                            color={saleDiscount ? undefined : 'muted'}
                            type="body-sm"
                        >
                            {t('seller.cartDiscount.saleDiscount')}
                        </Typography>
                        {saleDiscount ? (
                            <Chip
                                color={
                                    isSaleDiscountInvalid
                                        ? 'default'
                                        : 'warning'
                                }
                                {...getNodeTestIdProp(
                                    'seller-sale-discount-tag',
                                )}
                                size="sm"
                                variant={
                                    isSaleDiscountInvalid ? 'soft' : 'primary'
                                }
                            >
                                {formatDiscountValue(saleDiscount)}
                            </Chip>
                        ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        {saleDiscount ? (
                            <Typography
                                className="tabular-nums"
                                color={
                                    isSaleAmountUnknown ? 'muted' : undefined
                                }
                                {...getNodeTestIdProp(
                                    'seller-sale-discount-amount',
                                )}
                                type="body-sm"
                            >
                                {isSaleAmountUnknown
                                    ? unknown
                                    : `−${formatGel(saleDiscountAmount)}`}
                            </Typography>
                        ) : null}
                        <Button
                            aria-label={t(
                                saleDiscount
                                    ? 'seller.cartDiscount.editSale'
                                    : 'seller.cartDiscount.addSale',
                            )}
                            {...getNodeTestIdProp(
                                'seller-sale-discount-button',
                            )}
                            onPress={onSaleDiscount}
                            render={renderSaleDiscountButton}
                            size="sm"
                            variant={saleDiscount ? 'ghost' : 'outline'}
                        >
                            {t(
                                saleDiscount
                                    ? 'seller.cartDiscount.edit'
                                    : 'seller.cartDiscount.add',
                            )}
                            <ShortcutHint
                                placement="control"
                                shortcut={SALE_DISCOUNT_SHORTCUT}
                            />
                        </Button>
                    </div>
                </div>
            )}

            {discountNotice ? (
                <Alert
                    {...getNodeTestIdProp('seller-discount-notice')}
                    role="alert"
                    status="danger"
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Description>
                            {t(discountNotice.key, discountNotice.values)}
                        </Alert.Description>
                    </Alert.Content>
                </Alert>
            ) : null}

            <Separator className="my-1" />
            <div className="flex items-center justify-between">
                <Typography weight="semibold">{t('seller.total')}</Typography>
                <Typography
                    className="tabular-nums leading-none"
                    color={discountNotice ? 'muted' : undefined}
                    {...getNodeTestIdProp('seller-total')}
                    type="h4"
                    weight="bold"
                >
                    {discountNotice
                        ? t('seller.discount.unknown')
                        : formatGel(total)}
                </Typography>
            </div>
        </div>
    );
};
